import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

import SelectPlaceholder from '../selectPlaceholder';

export const GcpNetworkSelect = ({
  accountId,
  networkName,
  networkHostProject,
  onChange,
  disabledItems,
  title = 'Network',
  allowSharedNetworks = false,
  allowOnlyPeeredNetworks = false,
}: {
  accountId?: string;
  networkName?: string;
  networkHostProject?: string;
  onChange: (
    networkName: string,
    networkHostProject?: string,
    isSharedNetwork?: boolean
  ) => void;
  disabledItems: string[];
  title?: string;
  allowSharedNetworks?: boolean;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  if (!accountId) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerGcpNetworkSelect
      accountId={accountId}
      networkName={networkName}
      networkHostProject={networkHostProject}
      onChange={onChange}
      disabledItems={disabledItems}
      title={title}
      allowSharedNetworks={allowSharedNetworks}
      allowOnlyPeeredNetworks={allowOnlyPeeredNetworks}
    />
  );
};

interface NetworkOption {
  id: string;
  networkName: string;
  isShared: boolean;
  project?: string;
  isPeered?: boolean;
}

const InnerGcpNetworkSelect = ({
  accountId,
  networkName,
  networkHostProject,
  onChange,
  disabledItems,
  title,
  allowSharedNetworks,
  allowOnlyPeeredNetworks,
}: {
  accountId: string;
  networkName?: string;
  networkHostProject?: string;
  onChange: (
    networkName: string,
    networkHostProject?: string,
    isSharedNetwork?: boolean
  ) => void;
  disabledItems: string[];
  title: string;
  allowSharedNetworks?: boolean;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  const dal = useDAL();
  const { data: ownNetworks } = dal.restore.gcp.listNetworks(accountId);
  const { data: sharedNetworks } =
    dal.restore.gcp.listSharedNetworks(accountId);

  if (
    !ownNetworks?.networks?.length &&
    !(allowSharedNetworks && sharedNetworks?.networks?.length)
  ) {
    return <EmptySelect title={title} />;
  }

  const OwnOptionsMap: NetworkOption[] = [];
  const SharedOptionsMap: NetworkOption[] = [];

  (ownNetworks?.networks ?? []).forEach((n) => {
    OwnOptionsMap.push({
      id: `${n.networkName}-${n.project}`,
      networkName: n.networkName,
      isShared: false,
      project: n.project,
      isPeered: n.isPeered,
    });
  });

  if (allowSharedNetworks) {
    (sharedNetworks?.networks ?? []).forEach((n) => {
      SharedOptionsMap.push({
        id: `${n.networkName}-${n.project}`,
        networkName: n.networkName,
        isShared: true,
        project: n.project,
        isPeered: n.isPeered,
      });
    });
  }

  const options = [
    ...OwnOptionsMap.sort((a, b) => a.networkName.localeCompare(b.networkName)),
    ...SharedOptionsMap.sort((a, b) =>
      a.networkName.localeCompare(b.networkName)
    ),
  ];

  return (
    <FormControl size='small' className='flex-1'>
      <Stack
        className='flex-1 pr-[8px]'
        direction='row'
        alignItems='top'
        justifyContent='space-between'
      >
        <FormLabel>{title}</FormLabel>
        {allowOnlyPeeredNetworks && (
          <Tooltip
            title={
              'When using a private IP, only peered networks are available.'
            }
            slotProps={{
              tooltip: {
                sx: {
                  padding: '16px',
                },
              },
            }}
          >
            <i className='material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px] text-black' />
          </Tooltip>
        )}
      </Stack>
      <Select
        data-testid='networkSelect'
        size='small'
        value={
          networkName && networkHostProject
            ? `${networkName}-${networkHostProject}`
            : ''
        }
        onChange={(event) => {
          const selectedOption = options.find(
            (o) => o.id === event.target.value
          );
          if (selectedOption) {
            onChange(
              selectedOption.networkName,
              selectedOption.project,
              selectedOption.isShared
            );
          }
        }}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return <SelectPlaceholder />;
          }
          const network = options.find((o) => o.id === selected);
          if (!network) {
            return <SelectPlaceholder />;
          }
          return getNetworkLabel(network);
        }}
      >
        {options.map((value, idx) => (
          <MenuItem
            data-testid={`network-${idx}`}
            key={value.id}
            value={value.id}
            disabled={
              disabledItems.includes(value.networkName) ||
              (allowOnlyPeeredNetworks && !value.isPeered)
            }
          >
            {getNetworkLabel(value)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const getNetworkLabel = (network: NetworkOption) => {
  return network.isShared && network.project
    ? `${network.networkName} (Shared from ${network.project})`
    : network.networkName;
};
